/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes, { number } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHotkeys } from 'react-hotkeys-hook';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import MDDatePicker from 'components/MDDatePicker';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDInput from 'components/MDInput';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';

// Authentication layout components
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';
import cashPic from 'assets/images/cash.png';
import PosPrint from './PosPrint';

import './PointOfSale.css';

const initCart = {
  id: '',
  customer_id: '',
  customer_name: '',
  nettotal: '',
  discount_all: '',
  moneyReceive: '',
  moneyChange: '',
  shipping_id: '1',
  time_shipping: '00:00',
  car_id: '',
  brandsend_id: '',
  address: '',
  address2: '',
  date_record: Func.dateNow(),
  date_due: Func.dateNow(),
  date_shipping: Func.dateNow(),
};

function PosCash({
  reload,
  isDisabled,
  setBlockScan,
  sizeButton,
  isMobileDevice,
  buttonLoading,
  setButtonLoading,
}) {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [notify, setNotify] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalChange, setModalChange] = useState(false);
  const [summary, setSummary] = useState({});
  const [cart, setCart] = useState(initCart);
  const [cartList, setCartList] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const [tab, setTab] = useState(1);
  const txtMoney = useRef(null);
  const btnClose = useRef(null);
  const printRef = useRef();
  const btnRef = useRef(null);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const close = () => {
    setModalOpen(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const checkout = () => {
    setLoading(true);
    setBlockScan(true);
    setTab(1);

    FetchData('OPTIONS', `/${mod}/checkout`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          if (result.pharmacy === 'N') {
            showNotify('กรุณาเพิ่มข้อมูลลูกค้า', 'error');
          } else {
            setCart({ ...result.cart, moneyReceive: '', moneyChange: '' });
            setCartList(result.cart_list);
            setCampaign(result.campaign);
            setModalOpen(true);

            setTimeout(function () {
              txtMoney.current.focus();
            }, 500);
          }
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clear = () => {
    setLoading(true);
    const status = JSON.parse(localStorage.getItem('pos_status'));
    if (
      status?.step === 'print' &&
      status?.status === 'success' &&
      status?.paymentType === 'CASH'
    ) {
      localStorage.setItem(
        'pos_status',
        JSON.stringify({ ...status, step: 'change', status: 'success', paymentType: 'CASH' })
      );
    }

    FetchData('OPTIONS', `/${mod}/clear`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setModalChange(false);
          setBlockScan(false);
          setSummary({});
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changeNumber = (num) => {
    if (num === '-') {
      setCart({ ...cart, moneyReceive: '', moneyChange: '' });
      return;
    }

    const receive = cart.moneyReceive.concat(num);

    const nettotal = cart.pay_split === 'Y' ? cart.pay_cash * 1 : cart.nettotal * 1;
    let change = receive - nettotal;
    if (change <= 0) change = '';

    setCart({ ...cart, moneyReceive: receive.toString(), moneyChange: Func.numberDisplay(change) });
  };

  const changeNumberFix = (num) => {
    const receive = cart.moneyReceive * 1 + num;

    const nettotal = cart.pay_split === 'Y' ? cart.pay_cash * 1 : cart.nettotal * 1;
    let change = receive - nettotal;
    if (change <= 0) change = '';

    setCart({ ...cart, moneyReceive: receive.toString(), moneyChange: Func.numberDisplay(change) });
  };

  const payment = () => {
    if (loading) return;

    setPaymentLoading(true);

    if (
      cart.moneyReceive * 1 !== 0 &&
      ((cart.pay_split === 'N' && cart.moneyReceive * 1 < cart.nettotal * 1) ||
        (cart.pay_split === 'Y' && cart.moneyReceive * 1 < cart.pay_cash * 1))
    ) {
      showNotify('จำนวนเงินที่รับไม่เพียงพอ', 'error');
      setPaymentLoading(false);
      return;
    }

    if (
      cart.pay_split === 'N' ||
      (cart.pay_split === 'Y' && Number(cart.paid) + Number(cart.pay_cash) >= Number(cart.nettotal))
    ) {
      localStorage.setItem(
        'pos_status',
        JSON.stringify({
          step: 'payment',
          status: 'fetching',
          paymentType: 'CASH',
          cartId: cart.id,
        })
      );
    }

    FetchData('OPTIONS', `/${mod}/payment`, app.token, {
      payment_type: 'CASH',
      money_receive: cart.moneyReceive,
      money_change: cart.moneyChange,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setSummary(result.summary);
          setCart(initCart);
          setCartList([]);
          setCampaign([]);
          setModalOpen(false);
          setModalChange(true);
          reload();
          showNotify(t(`alert.addcomplete`), 'success');

          const status = JSON.parse(localStorage.getItem('pos_status'));
          if (
            status?.step === 'payment' &&
            status?.status === 'fetching' &&
            status?.paymentType === 'CASH'
          ) {
            delete status.cartId;
            localStorage.setItem(
              'pos_status',
              JSON.stringify({
                ...status,
                status: 'success',
                soId: result.id,
                summaryChange: result.summary,
              })
            );
          } else {
            showNotify('เกิดข้อผิดพลาดในการบันทึกสถานะ Local', 'error');
          }

          printRef.current.print(result.id);

          setTimeout(function () {
            btnClose.current.focus();
          }, 500);
        } else if (result.status === 'paid') {
          setCart(result.cart);
          close();
          reload();
          showNotify(t(`alert.addcomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setPaymentLoading(false);
      });
  };

  const btnNumPad = (num) => {
    return (
      <MDButton
        variant='text'
        color='info'
        size='large'
        sx={{ lineHeight: 3, fontSize: 20 }}
        fullWidth
        onClick={() => changeNumber(num)}
      >
        {num}
      </MDButton>
    );
  };

  const btnNumChange = (num) => {
    return (
      <MDButton
        variant='text'
        color='dark'
        size='large'
        sx={{ lineHeight: 3, fontSize: 20 }}
        fullWidth
        onClick={() => changeNumberFix(num)}
      >
        {num}
      </MDButton>
    );
  };

  const btnNumTotal = () => {
    const nettotal = cart.pay_split === 'Y' ? cart.pay_cash * 1 : cart.nettotal * 1;
    const checktotal = Math.floor(nettotal);

    return (
      <MDButton
        variant='text'
        color='dark'
        size='large'
        sx={{ lineHeight: 4, fontSize: 20 }}
        fullWidth
        onClick={() => changeNumberFix(nettotal)}
      >
        {nettotal === checktotal ? Func.numberDisplay(nettotal, 0) : Func.numberDisplay(nettotal)}
      </MDButton>
    );
  };

  const renderTextBox = () => {
    return (
      <MDBox>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <MDInput
              onChange={(e) => setCart({ ...cart, moneyReceive: e.target.value })}
              value={cart.moneyReceive}
              variant='outlined'
              type='number'
              fullWidth
              InputProps={{
                inputRef: txtMoney,
                maxLength: 50,
                autoFocus: true,
                startAdornment: (
                  <InputAdornment position='start'>
                    <MDTypography variant='h4' sx={{ width: 90 }}>
                      รับเงิน :
                    </MDTypography>
                  </InputAdornment>
                ),
                autoComplete: 'off',
                inputProps: {
                  autoComplete: 'off',
                  placeholder: '0',
                  className: 'txt-quantity',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              value={cart.moneyChange}
              variant='outlined'
              fullWidth
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <MDTypography variant='h4' sx={{ width: 90 }}>
                      เงินทอน :
                    </MDTypography>
                  </InputAdornment>
                ),
                autoComplete: 'off',
                inputProps: {
                  autoComplete: 'off',
                  placeholder: '0',
                  className: 'txt-change',
                },
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPromotion = () => {
    return (
      <TableContainer sx={{ height: 200, boxShadow: 0 }}>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>โปรโมชั่น</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaign.map((row, i) => (
              <TableRow className='row-order' key={row.id}>
                <TableCell width={80}>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSummary = () => {
    return (
      <TableContainer sx={{ boxShadow: 0 }}>
        <Table aria-label='summary table'>
          <TableBody>
            {cart.problem_type === 'return' ? (
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h5'>มูลค่าการคืน :</MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h5'>
                    {Func.numberDisplay(cart.return_total, 2)}
                  </MDTypography>
                </TableCell>
              </TableRow>
            ) : null}
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>รวม :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>{Func.numberDisplay(cart.total, 2)}</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>ส่วนลด :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>{Func.numberDisplay(cart.discount_all, 2)}</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>รวมทั้งสิ้น :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h4' color='info'>
                  {Func.numberDisplay(cart.nettotal, 2)}
                </MDTypography>
              </TableCell>
            </TableRow>
            {cart.pay_split === 'Y' ? (
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h5'>แยกชำระ :</MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h4' color='error'>
                    {Func.numberDisplay(cart.pay_cash, 2)}
                  </MDTypography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={0}>
          <Grid item xs={12} sx={{ borderBottom: '1px solid #cccccc;' }}>
            <TableContainer
              sx={{
                height: 335,
                boxShadow: 0,
              }}
            >
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>No.</TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align='center'>โปรโมชั่น</TableCell>
                    <TableCell align='right'>จำนวน</TableCell>
                    <TableCell align='right'>แถม</TableCell>
                    <TableCell align='left'>หน่วย</TableCell>
                    <TableCell align='right'>ราคา</TableCell>
                    <TableCell align='right'>รวม</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartList.map((row, i) => (
                    <TableRow className='row-order' key={row.id}>
                      <TableCell align='center'>{i + 1}</TableCell>
                      <TableCell>{row.product_name}</TableCell>
                      <TableCell align='center'>{row.campaign_code}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.quantity)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.quantity_free)}</TableCell>
                      <TableCell align='left'>{row.unit_name}</TableCell>
                      <TableCell align='right'>
                        {row.free === 'Y' ? '' : Func.numberDisplay(row.price)}
                      </TableCell>
                      <TableCell align='right'>
                        {row.free === 'Y' ? <code>แถม</code> : Func.numberDisplay(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} sm={12} md={7}>
            {renderPromotion()}
          </Grid>
          <Grid item xs={12} sm={12} md={5} sx={{ borderLeft: '1px solid #cccccc;' }}>
            {renderSummary()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderNumpad = () => {
    return (
      <MDBox className='card' mt={1}>
        <table width='100%'>
          <tbody>
            <tr>
              <td width='25%'>{btnNumPad(7)}</td>
              <td width='25%'>{btnNumPad(8)}</td>
              <td width='25%'>{btnNumPad(9)}</td>
              <td width='25%'>{btnNumChange(1000)}</td>
            </tr>
            <tr>
              <td>{btnNumPad(4)}</td>
              <td>{btnNumPad(5)}</td>
              <td>{btnNumPad(6)}</td>
              <td>{btnNumChange(500)}</td>
            </tr>
            <tr>
              <td>{btnNumPad(1)}</td>
              <td>{btnNumPad(2)}</td>
              <td>{btnNumPad(3)}</td>
              <td>{btnNumChange(100)}</td>
            </tr>
            <tr>
              <td>{btnNumPad('.')}</td>
              <td>{btnNumPad(0)}</td>
              <td>
                <MDButton
                  variant='text'
                  color='info'
                  size='large'
                  sx={{ lineHeight: 4 }}
                  fullWidth
                  onClick={() => changeNumber('-')}
                >
                  <Icon>backspace</Icon>
                </MDButton>
              </td>
              <td>{btnNumTotal()}</td>
            </tr>
          </tbody>
        </table>
      </MDBox>
    );
  };

  const renderButton = () => {
    return (
      <MDBox mt={1}>
        <MDButton
          variant='gradient'
          color='info'
          size='large'
          fullWidth
          onClick={payment}
          sx={{ fontSize: '1.5rem' }}
          disabled={paymentLoading}
        >
          {paymentLoading ? (
            <CircularProgress sx={{ fontSize: '1.5rem' }} color='inherit' />
          ) : (
            <img src={cashPic} width='30' alt='' />
          )}
          &nbsp; ชำระเงินสด
        </MDButton>
      </MDBox>
    );
  };

  const renderShipping = () => {
    return (
      <MDBox p={2} sx={{ height: 600 }}>
        <FormControl>
          <FormLabel id='demo-radio-buttons-group-label'>การจัดส่ง</FormLabel>
          <RadioGroup
            aria-labelledby='demo-radio-buttons-group-label'
            value={cart.shipping_id}
            onChange={(e) => setCart({ ...cart, shipping_id: e.target.value })}
          >
            <FormControlLabel value='1' control={<Radio />} label='รับของทันที่' />
            <FormControlLabel value='2' control={<Radio />} label='รับของที่สาขา' />

            <MDBox className='card' p={2}>
              <Grid container>
                {cart.shipping_id === '3' ? (
                  <Grid item xs={12}>
                    <MDDatePicker
                      onChange={([date]) => {
                        setCart({ ...cart, date_shipping: Func.dateFormat(date) });
                      }}
                      value={new Date(cart.date_shipping)}
                      input={{
                        label: 'วันส่งของ',
                        variant: 'standard',
                        fullWidth: true,
                      }}
                    />
                  </Grid>
                ) : null}
                {cart.shipping_id === '3' ? (
                  <Grid item xs={12}>
                    <MDInput
                      label='เวลาส่งของ'
                      type='time'
                      onChange={(e) => setCart({ ...cart, time_shipping: e.target.value })}
                      value={cart.time_shipping}
                      variant='standard'
                      inputProps={{
                        autoComplete: 'off',
                      }}
                      fullWidth
                    />
                  </Grid>
                ) : null}
                {/* {cart.shipping_id === "2" ? (
                  <Grid item xs={12}>
                    <MDSelect
                      label="สาขาจัดส่ง"
                      labelDefault={t(`element.please_select`)}
                      data={cboBranch}
                      value={cart.branchsend_id}
                      onChange={(e) => setCart({ ...cart, branchsend_id: e.target.value })}
                      inputProps={{
                        autoComplete: "off",
                      }}
                    />
                  </Grid>
                ) : null} */}
              </Grid>
            </MDBox>
            <FormControlLabel value='3' control={<Radio />} label='จัดส่งตามที่อยู่' />
            <FormControlLabel value='4' control={<Radio />} label='จัดส่งที่ Cargo' />
          </RadioGroup>
        </FormControl>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          <i className='fad fa-money-bill-alt color-danger' /> &nbsp; ชำระเงินสด
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          {tab === 1 ? (
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={6} lg={7} xl={8}>
                <MDBox className='card'>{renderList()}</MDBox>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
                <Grid container>
                  <Grid item xs={12}>
                    {renderTextBox()}
                  </Grid>
                  <Grid item xs={12}>
                    {renderNumpad()}
                  </Grid>
                  <Grid item xs={12}>
                    {renderButton()}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : tab === 2 ? (
            renderShipping()
          ) : null}
        </DialogContent>
        {/* <DialogActions>
          <Grid container>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-start" gap={1}>
                <MDButton
                  variant={tab === 1 ? "outlined" : "text"}
                  color="dark"
                  onClick={() => setTab(1)}
                >
                  <i className="fad fa-shopping-cart" /> &nbsp;รายการขาย
                </MDButton>
                <MDButton
                  variant={tab === 2 ? "outlined" : "text"}
                  color="dark"
                  onClick={() => setTab(2)}
                >
                  <i className="fad fa-shipping-fast" /> &nbsp;การจัดส่ง
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </DialogActions> */}
      </Dialog>
    );
  };

  const renderChange = () => {
    return (
      <Dialog
        open={modalChange}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-change'
        fullWidth
        onClose={clear}
      >
        <DialogTitle>
          <i className='fad fa-money-bill-alt color-danger' /> &nbsp; เงินทอน
          <IconButton
            aria-label='close'
            onClick={clear}
            autoFocus
            ref={btnClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <TableContainer sx={{ boxShadow: 0 }}>
            <Table aria-label='summary table'>
              <TableBody>
                <TableRow>
                  <TableCell align='right' width={200}>
                    <MDTypography variant='h2'>รวมทั้งสิ้น :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='info'>
                      {Func.numberDisplay(summary.nettotal)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h2'>รับเงิน :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='success'>
                      {Func.numberDisplay(summary.money_receive)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h2'>เงินทอน :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='error'>
                      {Func.numberDisplay(summary.money_change)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    );
  };

  useHotkeys('c', () => checkout());
  useHotkeys('x', () => {
    if (modalChange) clear();
  });

  return (
    <MDBox>
      <Tooltip title={isDisabled ? '' : 'KEY : C'}>
        <MDButton
          variant='contained'
          color='success'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={checkout}
          disabled={isDisabled || isMobileDevice || loading || buttonLoading}
          ref={btnRef}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-money-bill-alt' />
          )}
          &nbsp;
          {isMobileDevice ? 'ไม่รองรับบนมือถือ' : 'เงินสด'}
        </MDButton>
      </Tooltip>
      {renderOpen()}
      {renderChange()}
      <PosPrint ref={printRef} paymentType='CASH' />
      {notify}
    </MDBox>
  );
}

PosCash.propTypes = {
  reload: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  isMobileDevice: PropTypes.bool.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosCash;
